import { Share } from '@capacitor/share';
import { Clipboard } from '@capacitor/clipboard';
import { ref, computed, inject } from 'vue';
import useQuasar from 'quasar/src/composables/use-quasar/use-quasar.js';
import { stringParser } from 'src/plugins/string/string';
import { StandardAlertsPlugin } from '../alert/alertPlugin';
/**
 * Contains all share functions
 */
const sharePlugin = () => {
    const $q = useQuasar();
    const appType = inject('appType', 'getspaces');
    const shareMenu = ref(false);
    const menu = ref(false);
    /**
     * Alerts
     * @param text alert text
     */
    const { successAlert, errorAlert } = StandardAlertsPlugin();
    /**
     * Share and copy data functions
     * @param text text value to copy
     * @param type type of app ('app' or 'web')
     * @returns void
     */
    function copyToClipboard(text, type = 'web') {
        // function to copy data to clipboard
        if ((type === 'app') || ((typeof navigator !== 'undefined') &&
            ('clipboard' in navigator))) {
            void Clipboard.write({
                string: text
            });
            successAlert('Coplied to clipboard');
            shareMenu.value = false;
            menu.value = false;
            return;
        }
        else if (typeof document === 'undefined')
            return;
        const textArea = document.createElement('textarea');
        textArea.style.position = 'fixed';
        textArea.style.top = '0';
        textArea.style.left = '0';
        textArea.style.width = '2em';
        textArea.style.height = '2em';
        textArea.style.padding = '0';
        textArea.style.border = 'none';
        textArea.style.outline = 'none';
        textArea.style.boxShadow = 'none';
        textArea.style.background = 'transparent';
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
            const successful = document.execCommand('copy');
            if (successful) {
                successAlert('Copied to clipboard');
            }
            else {
                errorAlert('Oops, unable to copy');
            }
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        }
        catch (err) {
            errorAlert('Oops, unable to copy');
        }
        document.body.removeChild(textArea);
    }
    // check if platform supports share
    const showShare = computed(() => {
        if ($q.platform.is.capacitor) {
            return true;
        }
        if (typeof navigator !== 'undefined') {
            return 'canShare' in navigator;
        }
        return false;
    });
    /**
     * Sharing in capacitor mode
     * @param type share type
     * @param listing listing data to share
     * @param returnText text to add
     * @param fullPath full path of url to share
     * @returns void
     */
    function capacitorShare(type, listing, returnText, fullPath) {
        if (type === 'share') {
            void Share.share({
                title: listing.name,
                text: returnText,
                url: fullPath,
                dialogTitle: listing.name
            });
            return;
        }
        copyToClipboard(`${returnText}: ${fullPath}`, 'app');
    }
    /**
     * Share function. route to different types of share
     * accordingly
     * @param listing listing data
     * @param customText text to share
     * @param additionalQuery query of url
     * @param type type of share
     * @returns void
     */
    function share(listing, customText = '', additionalQuery = '', type = 'share') {
        let baseUrl = '';
        switch (process.env.prodType) {
            case 'prod':
                baseUrl = 'https://getspaces.com';
                break;
            case 'staging':
                baseUrl = 'https://getspaces.staging.retell.io';
                break;
            case 'dev':
                baseUrl = 'https://getspaces.dev.retell.io';
                break;
            default:
                baseUrl = window?.location.origin;
                break;
        }
        const listingPath = appType === 'getproperty'
            ? 'property'
            : 'listing';
        const parsedName = stringParser(listing.id.toString(), listing.name);
        const fullPath = `${baseUrl}/${listingPath}/${parsedName}${additionalQuery}`;
        const returnText = customText || `Check out ${listing.name}!`;
        if ($q.platform.is.capacitor) {
            capacitorShare(type, listing, returnText, fullPath);
            return;
        }
        // web app share
        if ((typeof navigator !== 'undefined') && showShare.value) {
            void navigator?.share({
                title: listing.name,
                text: returnText,
                url: fullPath
            });
            return;
        }
        copyToClipboard(`${returnText}: ${fullPath}`);
    }
    /**
     * Route to possible share menu
     * @param listing listing info
     * @param text text to share
     * @returns void
     */
    function shareRoute(listing, text = '') {
        // show share menu for android
        if ($q.platform.is.capacitor
            && $q.platform.is.android) {
            shareMenu.value = !shareMenu.value;
            return;
        }
        share(listing, text);
    }
    return {
        shareMenu,
        menu,
        showShare,
        copyToClipboard,
        share,
        shareRoute
    };
};
export { sharePlugin };
